import { auth } from "../../typings/auth";
import { STATE } from "../../thor.const";
import { OnlineContainerEditController } from "./online-container-edit-controller";
import { OnlineContainerItemsController } from "./items.controller";
import { setIframeEvents } from "../../sales/edit/sale-edit.routes";
import { Enums } from "../../thor.enums";

// Categories and checkAcl are injected here to ensure
// that they're resolved before resolving the sale
const stateResolveOnlineContainer = [
    "$log",
    "$stateParams",
    "onlineContainerEditModel",
    "checkAcl",
    "categories",
    ($log: ng.ILogService, $stateParams, onlineSaleEditModel) => {
        onlineSaleEditModel.setId($stateParams.id);

        return new Promise((resolve, reject) =>
            onlineSaleEditModel
                .fetchSale()
                .then(async () => {
                    // redirect sale Online to v2
                    if (!onlineSaleEditModel.resource.isPast) {
                        return window.open(
                            window.location.href
                                .replace(/online-containers/, "v2/sales")
                                .replace(/conditions/, "fees-and-conditions"),
                            "_self"
                        );
                    }
                    await onlineSaleEditModel.getSaleTranslationStatus();
                    await onlineSaleEditModel.setSubscriptionsCount();
                    await onlineSaleEditModel.getStripeContracts();
                    onlineSaleEditModel.checkIsReadOnly();
                    return resolve(onlineSaleEditModel);
                })
                .catch(error => {
                    $log.error(`Error while accessing online container content ${$stateParams.id}`, error);
                    return reject({ redirectStateName: STATE.V2.ERROR_404.name });
                })
        );
    }
];

export class OnlineContainerEditConfiguration {
    public static $inject: string[] = ["$stateProvider"];

    constructor($stateProvider: angular.ui.IStateProvider) {
        $stateProvider
            .state(STATE.ONLINE.EDIT.ABSTRACT, {
                abstract: true,
                url: "/online-containers/{id:[0-9]*}",
                resolve: {
                    // initUser is injected here to ensure that it's resolved before resolving acl permission
                    checkAcl: ["aclService", "initUser", aclService => aclService.isStateAllowed(auth.AclAccess.Sales)],
                    container: stateResolveOnlineContainer,
                    iframeEvents: ["$log", "$rootScope", "$window", "onlineContainerEditModel", setIframeEvents]
                },
                views: {
                    "": {
                        template: require("./index.html"),
                        controller: OnlineContainerEditController,
                        controllerAs: "onlineContainerEditCtrl"
                    },
                    "information@thor.online-container": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "onlineContainerEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.INFO.url(resource?.id)}?oId=${
                                    organization?.id
                                }`)
                        ]
                    },
                    "location@thor.online-container": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "onlineContainerEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.LOCATION.url(
                                    resource?.id
                                )}?oId=${organization?.id}`)
                        ]
                    },
                    "conditions@thor.online-container": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "onlineContainerEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.FEES_AND_CONDITIONS.url(
                                    resource?.id
                                )}?oId=${organization?.id}`)
                        ]
                    },
                    "shipping@thor.online-container": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "onlineContainerEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.SHIPPING.url(
                                    resource?.id
                                )}?oId=${organization?.id}`)
                        ]
                    },
                    "online@thor.online-container": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "onlineContainerEditModel",
                            async ($scope, { organization }, saleEditModel) => {
                                $scope.url = `${STATE.V2.SALES.SALE.ONLINE.url(saleEditModel.resource?.id)}?oId=${
                                    organization?.id
                                }`;
                                return await Promise.all([
                                    saleEditModel.fetchOnlineCaution(),
                                    saleEditModel.getStripeContracts()
                                ]);
                            }
                        ]
                    },
                    "items@thor.online-container": {
                        templateProvider: [
                            "authService",
                            "onlineContainerEditModel",
                            ({ organization }, saleEditModel) => {
                                saleEditModel.urlIframe = `${STATE.V2.SALES.SALE.ITEMS.url(
                                    saleEditModel.resource?.id
                                )}?oId=${organization?.id}`;
                                return require(saleEditModel.resource?.isPast
                                    ? "./items.html"
                                    : "./future-items-iframe.html");
                            }
                        ],
                        controller: OnlineContainerItemsController,
                        controllerAs: "onlineContainerItemsCtrl"
                    },
                    "subscriptions@thor.online-container": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "onlineContainerEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.SUBSCRIPTIONS.url(
                                    resource?.id,
                                    Enums.AuctionType.Online
                                )}?oId=${organization?.id}`)
                        ]
                    },
                    "stats@thor.online-container": {
                        template: require("../../shared/sale-edit/iframe.html"),
                        controller: [
                            "$scope",
                            "authService",
                            "onlineContainerEditModel",
                            ($scope, { organization }, { resource }) =>
                                ($scope.url = `${STATE.V2.SALES.SALE.STATS.url(resource?.id)}?oId=${organization?.id}`)
                        ]
                    }
                },
                onEnter: ["$rootScope", ($rootScope: ng.IRootScopeService) => ($rootScope.isRouteOnline = true)],
                onExit: [
                    "$rootScope",
                    "timingService",
                    ($rootScope: ng.IRootScopeService, timing) => {
                        $rootScope.isRouteOnline = false;
                        timing.stopInterval();
                    }
                ]
            })
            .state(STATE.ONLINE.EDIT.INFORMATION, {
                url: ""
            })
            .state(STATE.ONLINE.EDIT.LOCATION, {
                url: "/address"
            })
            .state(STATE.ONLINE.EDIT.CONDITIONS, {
                url: "/conditions"
            })
            .state(STATE.ONLINE.EDIT.SHIPPING, {
                url: "/shipping"
            })
            .state(STATE.ONLINE.EDIT.ONLINE, {
                url: "/online"
            })
            .state(STATE.ONLINE.EDIT.ITEMS, {
                url: "/lots"
            })
            .state(STATE.ONLINE.EDIT.SUBSCRIPTIONS, {
                url: "/subscriptions"
            })
            .state(STATE.ONLINE.EDIT.STATS, {
                url: "/stats"
            });
    }
}
